import React, { useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

export default function SearchableSelect({
    value,
    options = [],
    onChange,
    placeholder = "Pilih...",
    disabled = false,
    searchPlaceholder = "Cari...",
    emptyText = "Data tidak ditemukan.",
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    const filtered = useMemo(
        () => options.filter((opt) => opt.toLowerCase().includes(query.toLowerCase())),
        [options, query]
    );

    const handleSelect = (option) => {
        onChange(option);
        setQuery("");
        setOpen(false);
        if (inputRef.current) inputRef.current.blur();
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange("");
        setQuery("");
        if (inputRef.current) inputRef.current.focus();
    };

    const handleFocus = () => {
        if (!disabled) { setOpen(true); if (value) setQuery(""); }
    };

    const handleBlur = (e) => {
        if (containerRef.current && containerRef.current.contains(e.relatedTarget)) return;
        setOpen(false);
        setQuery("");
    };

    return (
        <div ref={containerRef} onBlur={handleBlur} style={{ position: "relative", width: "100%" }}>
            <div style={{ position: "relative" }}>
                <input
                    ref={inputRef}
                    type="text"
                    disabled={disabled}
                    value={open ? query : value}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={handleFocus}
                    placeholder={open ? searchPlaceholder : (value ? value : placeholder)}
                    style={{
                        width: "100%",
                        padding: "13px 48px 13px 16px",
                        border: open ? "2px solid #10b981" : "2px solid #e5e7eb",
                        borderRadius: 12,
                        background: disabled ? "#f9fafb" : "#fff",
                        fontSize: 14,
                        fontWeight: value && !open ? 500 : 400,
                        color: value && !open ? "#111827" : "#9ca3af",
                        outline: "none",
                        cursor: disabled ? "not-allowed" : "text",
                        opacity: disabled ? 0.5 : 1,
                        transition: "border-color 0.2s, box-shadow 0.2s",
                        boxShadow: open ? "0 0 0 4px rgba(16,185,129,0.08)" : "none",
                        boxSizing: "border-box",
                        fontFamily: "inherit",
                    }}
                />
                <div style={{
                    position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                    display: "flex", alignItems: "center", gap: 4, pointerEvents: disabled ? "none" : "auto",
                }}>
                    {value && !open && (
                        <button type="button" onClick={handleClear} style={{
                            background: "none", border: "none", cursor: "pointer", padding: 2,
                            color: "#9ca3af", display: "flex", alignItems: "center",
                            borderRadius: 4, transition: "color 0.15s",
                        }}
                            onMouseEnter={e => e.currentTarget.style.color = "#374151"}
                            onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
                        >
                            <X size={14} />
                        </button>
                    )}
                    <ChevronsUpDown size={16} color="#9ca3af" />
                </div>
            </div>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 999,
                    background: "#fff", border: "1.5px solid #e5e7eb",
                    borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                    maxHeight: 256, overflowY: "auto",
                }}>
                    {filtered.length === 0 ? (
                        <div style={{ padding: "12px 16px", fontSize: 13, color: "#9ca3af" }}>{emptyText}</div>
                    ) : (
                        filtered.map((option) => (
                            <button
                                key={option}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleSelect(option)}
                                style={{
                                    display: "flex", alignItems: "center", gap: 10,
                                    width: "100%", padding: "11px 16px",
                                    background: value === option ? "#f0fdf4" : "transparent",
                                    border: "none", cursor: "pointer", textAlign: "left",
                                    fontSize: 13, color: "#111827", fontFamily: "inherit",
                                    transition: "background 0.15s",
                                }}
                                onMouseEnter={e => { if (value !== option) e.currentTarget.style.background = "#f9fafb"; }}
                                onMouseLeave={e => { if (value !== option) e.currentTarget.style.background = "transparent"; }}
                            >
                                <span style={{ width: 16, flexShrink: 0 }}>
                                    {value === option && <Check size={14} color="#10b981" />}
                                </span>
                                <span style={{ lineHeight: 1.4 }}>{option}</span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
